/**
 * WebSocket hook for bi-directional communication with backend
 *
 * Enables:
 * - Backend to send explore queries for DuckDB execution
 * - Frontend to send back query results
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useDuckDBContext } from '../context/DuckDBContext';

// Maximum time to wait for DuckDB to initialize (ms)
const DUCKDB_INIT_TIMEOUT = 10000;
// Polling interval to check DuckDB status (ms)
const DUCKDB_POLL_INTERVAL = 100;

interface ExploreQueryMessage {
  type: 'explore_query';
  request_id: string;
  session_id: string;
  sql: string;
  table: string;
}

interface ExploreResultMessage {
  type: 'explore_result';
  request_id: string;
  success: boolean;
  rows?: Record<string, unknown>[];
  columns?: { name: string; type: string }[];
  row_count?: number;
  error?: string;
}

interface RegisterMessage {
  type: 'register';
  session_id: string;
}

interface DataStoredMessage {
  type: 'data_stored';
  session_id: string;
  table_name: string;
}

type WsIncoming = ExploreQueryMessage;

export function useWebSocket(sessionId: string | null) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const { executeQuery, isInitialized: isDuckDBReady } = useDuckDBContext();

  // Use refs to avoid stale closures in WebSocket handlers
  const isDuckDBReadyRef = useRef(isDuckDBReady);
  const executeQueryRef = useRef(executeQuery);
  const sessionIdRef = useRef(sessionId);

  // Keep refs in sync with latest values
  useEffect(() => {
    isDuckDBReadyRef.current = isDuckDBReady;
  }, [isDuckDBReady]);

  useEffect(() => {
    executeQueryRef.current = executeQuery;
  }, [executeQuery]);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // Wait for DuckDB to be initialized with timeout
  const waitForDuckDB = useCallback(async (): Promise<boolean> => {
    if (isDuckDBReadyRef.current) return true;

    const startTime = Date.now();
    while (Date.now() - startTime < DUCKDB_INIT_TIMEOUT) {
      if (isDuckDBReadyRef.current) return true;
      await new Promise(resolve => setTimeout(resolve, DUCKDB_POLL_INTERVAL));
    }
    return false;
  }, []);

  // Handle incoming messages - uses refs to always get latest executeQuery
  const handleMessage = useCallback(async (event: MessageEvent) => {
    try {
      const message: WsIncoming = JSON.parse(event.data);
      console.log('[WS] Received:', message.type);

      if (message.type === 'explore_query') {
        const { request_id, sql } = message;
        console.log('[WS] Executing explore query:', sql);

        try {
          // Wait for DuckDB to be ready before executing
          const ready = await waitForDuckDB();
          if (!ready) {
            const response: ExploreResultMessage = {
              type: 'explore_result',
              request_id,
              success: false,
              error: 'DuckDB initialization timed out',
            };
            wsRef.current?.send(JSON.stringify(response));
            return;
          }

          // Use ref to get the latest executeQuery function
          const result = await executeQueryRef.current(sql);

          const response: ExploreResultMessage = {
            type: 'explore_result',
            request_id,
            success: result.success,
            rows: result.success ? result.rows : undefined,
            columns: result.success ? result.columns : undefined,
            row_count: result.success ? result.rowCount : undefined,
            error: result.success ? undefined : result.error,
          };

          console.log('[WS] Sending result:', response.success, response.row_count);
          wsRef.current?.send(JSON.stringify(response));
        } catch (err) {
          const response: ExploreResultMessage = {
            type: 'explore_result',
            request_id,
            success: false,
            error: err instanceof Error ? err.message : 'Unknown error executing query',
          };
          wsRef.current?.send(JSON.stringify(response));
        }
      }
    } catch (err) {
      console.error('[WS] Error handling message:', err);
    }
  }, [waitForDuckDB]);

  // Connect to WebSocket - stable function that uses refs
  const connect = useCallback(() => {
    const currentSessionId = sessionIdRef.current;
    if (!currentSessionId) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    // Determine WebSocket URL based on current location
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = process.env.REACT_APP_API_URL
      ? new URL(process.env.REACT_APP_API_URL).host
      : window.location.host.replace(':3000', ':3001');
    const wsUrl = `${protocol}//${host}/ws`;

    console.log('[WS] Connecting to:', wsUrl);

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[WS] Connected');
      setIsConnected(true);

      // Register with session ID (use ref for latest value)
      const registerMsg: RegisterMessage = {
        type: 'register',
        session_id: sessionIdRef.current || currentSessionId,
      };
      ws.send(JSON.stringify(registerMsg));
      console.log('[WS] Registered session:', registerMsg.session_id);
    };

    ws.onmessage = handleMessage;

    ws.onclose = (event) => {
      console.log('[WS] Disconnected, code:', event.code);
      setIsConnected(false);
      wsRef.current = null;

      // Only reconnect if not a normal closure (code 1000)
      if (event.code !== 1000) {
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('[WS] Reconnecting...');
          connect();
        }, 3000);
      }
    };

    ws.onerror = (err) => {
      console.error('[WS] Error:', err);
    };
  }, [handleMessage]); // Remove sessionId from deps - use ref instead

  // Connect on mount only (stable - doesn't depend on sessionId)
  useEffect(() => {
    // Initial connection if we have a session
    if (sessionIdRef.current) {
      connect();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        // Use code 1000 for normal closure to prevent auto-reconnect
        wsRef.current.close(1000, 'Component unmount');
        wsRef.current = null;
      }
    };
  }, [connect]);

  // Re-register when sessionId changes (don't close/reopen connection)
  useEffect(() => {
    if (sessionId && wsRef.current?.readyState === WebSocket.OPEN) {
      // Re-register with new session ID on existing connection
      const registerMsg: RegisterMessage = {
        type: 'register',
        session_id: sessionId,
      };
      wsRef.current.send(JSON.stringify(registerMsg));
      console.log('[WS] Re-registered session:', sessionId);
    } else if (sessionId && !wsRef.current) {
      // No connection yet, establish one
      connect();
    }
  }, [sessionId, connect]);

  // Send data_stored message to backend when data is stored in DuckDB
  const sendDataStored = useCallback((tableName: string) => {
    if (!sessionId || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn('[WS] Cannot send data_stored: not connected');
      return;
    }

    const msg: DataStoredMessage = {
      type: 'data_stored',
      session_id: sessionId,
      table_name: tableName,
    };
    wsRef.current.send(JSON.stringify(msg));
    console.log('[WS] Sent data_stored:', tableName);
  }, [sessionId]);

  return { isConnected, sendDataStored };
}
